/**
 * weeklySummaryOverrides — persisted authoritative cache for recently
 * saved weekly summary entries, used to defend against Cloudflare KV's
 * 60-second read cache returning stale data.
 *
 * The problem:
 *   Cloudflare KV has a minimum read-cache TTL of 60 seconds. When you
 *   save a weekly summary and then close/reopen the browser, the fresh
 *   GET /meta may be served by a different worker instance whose KV
 *   cache still has the pre-save version. Your just-saved content
 *   appears to revert.
 *
 * The fix:
 *   After each save we write the authoritative entry + its savedAt to
 *   localStorage. When rendering, we compare the override's savedAt to
 *   whatever the server returned:
 *     - override.savedAt > server.savedAt → server is stale, use override
 *     - override.savedAt <= server.savedAt → server has the latest (this
 *       might be a newer edit from another user), use server
 *   Entries older than 90s are dropped so we never mask long-term
 *   drift from real-world data.
 */

const OVERRIDE_TTL_MS = 90_000
const STORAGE_PREFIX = 'ws-override:'

const memoryOverrides = new Map() // fast path

function storage() {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null
  } catch {
    return null
  }
}

export function rememberAuthoritative(goalKey, entry) {
  if (!goalKey || !entry) return
  const record = { entry, at: Date.now() }
  memoryOverrides.set(goalKey, record)
  const s = storage()
  if (s) {
    try {
      s.setItem(STORAGE_PREFIX + goalKey, JSON.stringify(record))
    } catch {}
  }
}

/**
 * Return the authoritative entry for `goalKey` if we have one that is
 * fresh AND newer than the serverEntry (or if serverEntry is null).
 * Otherwise return null and let the caller fall back to server state.
 *
 * Callers pass in `serverEntry` so we can resolve the stale-vs-fresh
 * comparison in one place.
 */
export function getAuthoritative(goalKey, serverEntry) {
  // Memory first, fall back to localStorage
  let record = memoryOverrides.get(goalKey)
  if (!record) {
    const s = storage()
    if (s) {
      try {
        const raw = s.getItem(STORAGE_PREFIX + goalKey)
        if (raw) {
          record = JSON.parse(raw)
          memoryOverrides.set(goalKey, record)
        }
      } catch {}
    }
  }
  if (!record) return null

  // TTL check: drop stale overrides so we don't mask long-term edits.
  // The `Number.isFinite` guard catches the NaN case — a malformed
  // `record.at` (corrupted localStorage, manual tampering) would
  // otherwise make `Date.now() - NaN` = NaN, `NaN >= anything` = false,
  // and the override would persist forever, silently masking real
  // server data for that goal.
  if (!Number.isFinite(record.at) || Date.now() - record.at >= OVERRIDE_TTL_MS) {
    clearAuthoritative(goalKey)
    return null
  }

  // Compare savedAt: if server is newer, trust server (someone else
  // may have edited after our save)
  const serverAt = serverEntry?.savedAt || 0
  const overrideAt = record.entry?.savedAt || 0
  if (serverAt >= overrideAt) {
    // Server caught up or has newer data; stop overriding
    clearAuthoritative(goalKey)
    return null
  }

  return record.entry
}

export function clearAuthoritative(goalKey) {
  memoryOverrides.delete(goalKey)
  const s = storage()
  if (s) {
    try { s.removeItem(STORAGE_PREFIX + goalKey) } catch {}
  }
}
